import Overlay from 'ol/Overlay';
import h from 'hyperscript';
import { unByKey as olUnByKey } from 'ol/Observable';

import { assert, assertNonNullable } from 'cadenza/utils/custom-error';
import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey as browserUnByKey } from 'cadenza/utils/event-util';

import type { Positioning } from 'ol/Overlay';
import type { EventsKey } from 'ol/events';
import type Map from 'ol/Map';
import type { Coordinate } from 'ol/coordinate';

import './tooltip-overlay.css';

const DEFAULT_OFFSET = 15;

const HORIZONTAL_INSET = 7;

const DEFAULT_POSITIONING: Positioning = 'bottom-right';

const SHOW_ARROW_CLASS_PREFIX = 'tooltip--with-arrow';

export type Coloring = 'primary' | 'secondary';

export interface Options {
  message?: string;
  offset?: number[];
  positioning?: Positioning;
  position?: Coordinate;
  followMouse?: boolean;
  showArrow?: boolean;
  coloring?: Coloring;
}

export class TooltipOverlay {

  readonly #map: Map;
  #overlay: Overlay | undefined;
  #browserEventKeys: EventKey[] | undefined;
  #olEventKeys: EventsKey[] | undefined;
  #offset: number[] | undefined;
  #positioning: Positioning;
  #position: Coordinate;
  #showArrow: boolean;
  #followMouse: boolean;
  #message: string;
  #visible: boolean;
  #coloring;

  constructor (map: Map, options: Options = {}) {
    this.#map = map;
    this.#followMouse = options.followMouse ?? true;
    this.#showArrow = options.showArrow ?? false;
    this.#message = options.message ?? '';
    this.#positioning = options.positioning ?? DEFAULT_POSITIONING;
    this.#position = options.position ?? [ 0, 0 ];
    this.#offset = options.offset;
    this.#visible = false;
    this.#coloring = options.coloring;
  }

  activate () {
    assert(!this.#isActive(), 'Tooltip is active already.');
    this.#overlay = createOverlay();
    this.message = this.#message;
    this.offset = this.#offset;
    this.positioning = this.#positioning;
    this.position = this.#position;
    this.showArrow = this.#showArrow;
    this.followMouse = this.#followMouse;
    this.coloring = this.#coloring;
    this.#map.addOverlay(this.#overlay);
  }

  #isActive (): boolean {
    return this.#overlay !== undefined;
  }

  deactivate () {
    const overlay = this.#overlay;
    assertNonNullable(overlay, 'Overlay Tooltip is not active.');
    const overlayElement = overlay.getElement();
    this.#unbindMouseFollow();
    overlay.setElement(undefined);
    this.#map.removeOverlay(overlay);
    overlayElement?.remove();
    this.#overlay = undefined;
  }

  get followMouse (): boolean {
    return this.#followMouse;
  }

  set followMouse (value: boolean) {
    if (value) {
      this.#bindMouseFollow();
    } else {
      this.#unbindMouseFollow();
    }
    this.#followMouse = value;
  }

  #bindMouseFollow () {
    this.#browserEventKeys = [
      on(this.#map.getViewport(), 'mouseout', () => this.#setVisibilityToHTMLElement(false)),
      on(this.#map.getViewport(), 'mouseover', () => {
        this.visible = this.#visible;
      })
    ];
    this.#olEventKeys = [
      this.#map.on('pointermove', (event) => {
        this.position = event.coordinate;
      })
    ];
  }

  #unbindMouseFollow () {
    browserUnByKey(this.#browserEventKeys);
    olUnByKey(this.#olEventKeys!);
  }

  show () {
    this.visible = true;
  }

  hide () {
    this.visible = false;
  }

  get visible (): boolean {
    return this.#visible;
  }

  set visible (value: boolean) {
    this.#setVisibilityToHTMLElement(value);
    this.#visible = value;
  }

  #setVisibilityToHTMLElement (visible: boolean) {
    const element = this.#overlay?.getElement();
    if (!element) {
      return;
    }
    element.hidden = !visible;
  }

  get message (): string {
    return this.#message;
  }

  set message (value: string) {
    this.#setMessageToHTMLElement(value);
    this.#message = value;
  }

  #setMessageToHTMLElement (message: string) {
    const element = this.#overlay?.getElement();
    if (!element) {
      return;
    }
    element.textContent = message;
  }

  get position (): Coordinate {
    return this.#position;
  }

  set position (value: Coordinate) {
    if (this.#overlay) {
      this.#overlay.setPosition(value);
    }
    this.#position = value;
  }

  get positioning (): Positioning {
    return this.#positioning;
  }

  set positioning (value: Positioning) {
    if (this.#overlay) {
      const olPositioning = convertToOLOverlayPositioning(value);
      this.#overlay.setPositioning(olPositioning);
    }
    this.#positioning = value;
    this.showArrow = this.#showArrow;
    this.offset = this.#offset;
  }

  get offset (): number[] | undefined {
    return this.#offset;
  }

  set offset (value: number[] | undefined) {
    if (this.#overlay) {
      this.#overlay.setOffset(value ?? getDefaultOffsetForPositioning(this.#positioning));
    }
    this.#offset = value;
  }

  get showArrow (): boolean {
    return this.#showArrow;
  }

  set showArrow (value: boolean) {
    const element = this.element;
    if (element) {
      // remove class
      const prefix = SHOW_ARROW_CLASS_PREFIX;
      const classes = element.className.split(' ')
        .filter(aClass => !aClass.startsWith(prefix));
      element.className = classes.join(' ').trim();
      // add new class
      if (value) {
        element.classList.add(getArrowClass(this.positioning));
      }
    }
    this.#showArrow = value;
  }

  get coloring () {
    return this.#coloring;
  }

  set coloring (value) {
    this.element?.classList.toggle('tooltip-secondary', value === 'secondary');
    this.#coloring = value;
  }

  get element () {
    return this.#overlay?.getElement();
  }

}

function createOverlay (): Overlay {
  return new Overlay({
    element: h<HTMLElement>('.tooltip', { hidden: true }),
    // to render new overlays on top of existing ones
    insertFirst: false
  });
}

function getArrowClass (positioning: Positioning): string {
  switch (positioning) {
    case 'bottom-left':
      return 'tooltip--with-arrow-top-right';
    case 'bottom-center':
      return 'tooltip--with-arrow-top';
    case 'bottom-right':
      return 'tooltip--with-arrow-top-left';
    case 'center-left':
      return 'tooltip--with-arrow-right';
    case 'center-center':
      return '';
    case 'center-right':
      return 'tooltip--with-arrow-left';
    case 'top-left':
      return 'tooltip--with-arrow-bottom-right';
    case 'top-center':
      return 'tooltip--with-arrow-bottom';
    case 'top-right':
      return 'tooltip--with-arrow-bottom-left';
  }
}

/**
 * Openlayers has inverted coordinates,
 * and this overlay uses more human friendly descriptions.
 * Thus, we convert them before passing them to the internal ol-overlay.
 *
 * @param positioning
 */
function convertToOLOverlayPositioning (positioning: Positioning): Positioning {
  switch (positioning) {
    case 'bottom-left':
      return 'top-right';
    case 'bottom-center':
      return 'top-center';
    case 'bottom-right':
      return 'top-left';
    case 'center-left':
      return 'center-right';
    case 'center-center':
      return 'center-center';
    case 'center-right':
      return 'center-left';
    case 'top-left':
      return 'bottom-right';
    case 'top-center':
      return 'bottom-center';
    case 'top-right':
      return 'bottom-left';
  }
}

/**
 * Returns the default offset, depending on positioning.
 * Used, only if no offset defined by user.
 *
 * @param positioning
 */
function getDefaultOffsetForPositioning (positioning: Positioning) {
  switch (positioning) {
    case 'center-center':
      return [ 0, 0 ];
    case 'center-right':
      return [ DEFAULT_OFFSET, 0 ];
    case 'center-left':
      return [ -DEFAULT_OFFSET, 0 ];
    case 'top-center':
      return [ 0, -DEFAULT_OFFSET ];
    case 'top-right':
      return [ -HORIZONTAL_INSET, -DEFAULT_OFFSET ];
    case 'top-left':
      return [ HORIZONTAL_INSET, -DEFAULT_OFFSET ];
    case 'bottom-center':
      return [ 0, DEFAULT_OFFSET ];
    case 'bottom-right':
      return [ -HORIZONTAL_INSET, DEFAULT_OFFSET ];
    case 'bottom-left':
      return [ HORIZONTAL_INSET, DEFAULT_OFFSET ];
  }
}
